import { useMemo } from "react";
import { Box } from "@mui/material";
import { CustomDrawer, FormFooter, FormTextField } from "@shared/components";
import { Form, Formik, FormikProps } from "formik";
import {
  PublisherFormFields,
  PublisherFormInitialValues,
  PublisherValidationSchema,
} from "../utils/constants";
import {
  AddUpdatePublisherFormProps,
  PublisherFormValues,
} from "../utils/types";
import PublisherSwitches from "./PublisherSwitches";

const PublisherForm = ({
  open,
  onClose,
  title,
  showCloseButton,
  initialValues,
  onSubmit,
  disable,
}: AddUpdatePublisherFormProps) => {
  const mergedInitialValues = useMemo(
    () => ({
      ...PublisherFormInitialValues,
      ...initialValues,
    }),
    [initialValues]
  );

  const handleChange = (
    formikProps: FormikProps<PublisherFormValues>,
    name: string,
    isAccount: boolean
  ) => {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (isAccount) {
        const raw = e.target.value;
        // keep only digits and cap at 10 characters due to backend limit
        const digitsOnly = raw.replace(/\D/g, "");
        const limited = digitsOnly.slice(0, 9);

        if (limited === "") {
          formikProps.setFieldValue(name, "");
        } else {
          formikProps.setFieldValue(name, Number(limited));
        }
      } else {
        formikProps.handleChange(e);
      }
    };
  };

  const formatValue = (value: unknown, isAccount: boolean): string | number => {
    if (isAccount) {
      if (value === undefined || value === null || value === "") {
        return "";
      }
      return Number(value);
    }

    return String(value ?? "");
  };

  return (
    <CustomDrawer
      open={open}
      onClose={onClose}
      title={title}
      showCloseButton={showCloseButton}
    >
      <Formik
        enableReinitialize
        initialValues={mergedInitialValues}
        validationSchema={PublisherValidationSchema}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Form
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              overflow: "hidden",
              paddingBottom: "80px",
            }}
          >
            <Box
              sx={{
                position: "relative",
                p: 2,
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {PublisherFormFields.map(({ name, label, type }) => {
                const isAccount = name === "account";
                const value = formikProps.values[
                  name as keyof PublisherFormValues
                ] as unknown;
                return (
                  <FormTextField
                    key={name}
                    type={type}
                    name={name}
                    label={label}
                    value={formatValue(value, isAccount)}
                    onChange={handleChange(formikProps, name, isAccount)}
                    touched={formikProps.touched as Record<string, unknown>}
                    errors={formikProps.errors as Record<string, unknown>}
                  />
                );
              })}
              <PublisherSwitches formikProps={formikProps} />
            </Box>
            <FormFooter
              disable={disable || !formikProps.dirty}
              isSubmitting={formikProps.isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </CustomDrawer>
  );
};

export default PublisherForm;
